import {nest, withProps} from 'recompose'
import {DelayRender} from 'views/Shared/components/DelayRender'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Checkbox, Tooltip} from 'antd'
import {MinusOutlined} from '@ant-design/icons'
import WorkspaceContext from '../WorkspaceContext'
import useTranslate from 'modules/local/useTranslate'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {entityRole_getList_Api, entityRole_getSchema_Api, entityRole_setPermissions_Api,} from 'apis/entityRole'
import {getSystemRoles, SYSTEM_ROLE_IDS} from './consts/systemRoles'
import _ from 'lodash'
import classNames from "classnames";
import {PiLockKeyFill} from "react-icons/pi";
import {BsCheck, BsDash} from "react-icons/bs";

// ---------------------------------------------------------------------------
// SystemRolePermissions – quyền hard-code cho 3 system roles
// ---------------------------------------------------------------------------
const SYSTEM_PERMISSIONS = {
    [SYSTEM_ROLE_IDS.OWNER]: null,         // full – mọi key đều true (kể cả owner_only)
    [SYSTEM_ROLE_IDS.ADMIN]: {
        _exclude: ['organization_manage_role'],  // full trừ manage_role (owner_only)
    },
    [SYSTEM_ROLE_IDS.MEMBER]: null,        // sentinel: dùng symbol riêng
}

// Sentinel cho member (phân biệt với custom role không tìm thấy)
const MEMBER_SENTINEL = Symbol('member')
const RESOLVED_SYSTEM = {
    [SYSTEM_ROLE_IDS.OWNER]: null,
    [SYSTEM_ROLE_IDS.ADMIN]: { _exclude: ['organization_manage_role'] },
    [SYSTEM_ROLE_IDS.MEMBER]: MEMBER_SENTINEL,
}

const resolveSystemPerm = (roleId, permKey, ownerOnly = false) => {
    const rule = RESOLVED_SYSTEM[roleId]

    // owner → full kể cả owner_only
    if (rule === null) return 'full'

    // member → tất cả false
    if (rule === MEMBER_SENTINEL) return false

    // admin → owner_only keys luôn false (partial display)
    if (ownerOnly) return 'partial'

    // admin → excluded keys → partial (read-only, không check)
    if (rule._exclude?.includes(permKey)) return 'partial'

    return 'full'
}

// ---------------------------------------------------------------------------
// CheckboxCell
// ---------------------------------------------------------------------------
const CheckboxCell = ({value, disabled, coming_soon, onChange}) => {
    if (coming_soon) {
        return (
            <Tooltip title="Đang phát triển">
                <span className="flex justify-center">
                    <MinusOutlined className="text-gray-300 text-base"/>
                </span>
            </Tooltip>
        )
    }

    if (disabled) {
        if (value === 'full') {
            return (
                <span className="flex justify-center">
                    <span className="w-4 h-4 flex flex-center bg-primary rounded">
                        <BsCheck size={16} className="text-white"/>
                    </span>
                </span>
            )
        }
        if (value === 'partial') {
            return (
                <span className="flex justify-center">
                    <span className="w-4 h-4 flex flex-center border-2 border-primary rounded">
                        <BsDash size={16} className="text-primary"/>
                    </span>
                </span>
            )
        }
        return (
            <span className="flex justify-center">
                <span className="w-4 h-4 border border-gray-400 rounded"/>
            </span>
        )
    }

    return (
        <span className="flex justify-center">
            <Checkbox
                checked={!!value}
                onChange={(e) => onChange && onChange(e.target.checked)}
            />
        </span>
    )
}

// ---------------------------------------------------------------------------
// OrganizationPermissionMatrix
// ---------------------------------------------------------------------------
const OrganizationPermissionMatrix = ({onAddRole}) => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)

    const [schema, setSchema] = useState({})          // { key: { label, group, coming_soon } }
    const [customRoles, setCustomRoles] = useState([]) // [{ id, name, permissions: {key:bool} }]
    const [activeRoleId, setActiveRoleId] = useState(null)
    const saveTimers = useRef({})

    const onError = notifyOnError(t)

    // ── Load schema ──────────────────────────────────────────────────────────
    const {handleAsyncAction: loadSchema} = useAsyncAction({
        onError,
        apiInfo: entityRole_getSchema_Api,
        onSuccess: (result, data) => {
            const raw = _.get(data, 'response.data.schema', {})
            setSchema(raw)
        },
    })

    // ── Load custom roles list ───────────────────────────────────────────────
    const {handleAsyncAction: loadRoles} = useAsyncAction({
        onError,
        apiInfo: entityRole_getList_Api,
        onSuccess: (result, data) => {
            const items = _.get(data, 'response.data.data', [])
            setCustomRoles(items)
            if (!activeRoleId && items.length > 0) {
                setActiveRoleId(items[0].id)
            }
        },
    })

    useEffect(() => {
        if (!organization?.id) return
        loadSchema({}, {type: 'organization', id: organization.id})
        loadRoles({}, bindQueryParams([
            {type: 'organization'},
            {id: organization.id},
        ]))
    }, [organization?.id])

    // ── Save permissions (debounce 600ms) ────────────────────────────────────
    const {handleAsyncAction: doSave} = useAsyncAction({
        onError,
        apiInfo: entityRole_setPermissions_Api,
        onSuccess: () => successNotify(NotificationActionTypes.UPDATE, t),
    })

    const scheduleSave = useCallback((roleId, permissions) => {
        if (saveTimers.current[roleId]) {
            clearTimeout(saveTimers.current[roleId])
        }
        saveTimers.current[roleId] = setTimeout(() => {
            doSave({permissions: JSON.stringify(permissions)}, {role_id: roleId})
        }, 600)
    }, [doSave])

    // Toggle 1 permission key của 1 custom role
    const handleToggle = useCallback((roleId, permKey, newValue) => {
        setCustomRoles((prev) =>
            prev.map((role) => {
                if (role.id !== roleId) return role
                const updated = {...(role.permissions ?? {}), [permKey]: newValue}
                scheduleSave(roleId, updated)
                return {...role, permissions: updated}
            })
        )
    }, [scheduleSave])

    // ── System roles ─────────────────────────────────────────────────────────
    const systemRoles = useMemo(() => getSystemRoles(t), [t])

    // Tất cả roles để render cột (system + custom)
    const allRoles = useMemo(() => [
        ...systemRoles,
        ...customRoles,
    ], [systemRoles, customRoles])

    // Role đang được filter (null = hiển thị tất cả)
    const visibleRoles = useMemo(() => {
        if (!activeRoleId) return allRoles
        return allRoles.filter((r) => r.id === activeRoleId)
    }, [allRoles, activeRoleId])

    // Group schema theo group
    const groupedSchema = useMemo(() => {
        return Object.entries(schema).reduce((acc, [key, def]) => {
            const group = def.group || 'other'
            if (!acc[group]) acc[group] = []
            acc[group].push({key, ...def})
            return acc
        }, {})
    }, [schema])

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="h-full flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col">
                <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                    {t('permission')}
                </h2>
                <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                <div className="mt-1 text-sm text-color-300 tracking-wider">
                    {t('configure permission limits for each role in the community')}
                </div>
            </div>
            <div className="flex flex-col gap-3">
                {/* Role filter tabs */}
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={() => setActiveRoleId(null)}
                        className={classNames(
                            'px-3 py-1 rounded-full background border text-xs transition-colors',
                            !activeRoleId
                                ? 'border-secondary text-secondary-600 font-medium'
                                : 'border-color-100 hover:border-color-base text-gray-500 hover:text-gray-700 hover:shadow-sm',
                        )}>
                        {t('all')}
                    </button>
                    {allRoles.map((role) => (
                        <button
                            key={role.id}
                            onClick={() => setActiveRoleId(role.id === activeRoleId ? null : role.id)}
                            className={classNames(
                                'px-3 py-1 flex items-center rounded-full background border text-xs transition-colors',
                                activeRoleId === role.id
                                    ? 'border-secondary text-secondary-600 font-medium'
                                    : 'border-color-100 hover:border-color-base text-gray-500 hover:text-gray-700 hover:shadow-sm',
                            )}>
                            {role.is_system && <PiLockKeyFill size={14} className="mr-1"/>}
                            {role.name}
                        </button>
                    ))}
                </div>
                {/* Table */}
                <div className="overflow-x-auto flex-1">
                    {Object.keys(schema).length === 0 ? (
                        <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
                            {t('loading')}…
                        </div>
                    ) : (
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-gray-100 background-200">
                                    <th className="text-left px-4 py-2 font-medium text-color-100 w-56 whitespace-no-wrap sticky top-0 z-10">
                                        {t('feature')}
                                    </th>
                                    {visibleRoles.map((role) => (
                                        <th
                                            key={role.id}
                                            className="px-3 py-2 font-medium sticky top-0 z-10">
                                            <div className="text-center">
                                                <span>{role.name}</span>
                                                {role.is_system && (
                                                    <PiLockKeyFill className="ml-1 text-gray-400"/>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                            {Object.entries(groupedSchema).map(([group, items]) => (
                                <React.Fragment key={group}>
                                    {/* Group header row */}
                                    <tr className="bg-gray-300">
                                        <td
                                            colSpan={visibleRoles.length + 1}
                                            className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-widest">
                                            {t(group)}
                                        </td>
                                    </tr>
                                    {items.map(({key, label, coming_soon, owner_only, ...def}) => (
                                        <tr
                                            key={key}
                                            className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                                            <td className="px-4 py-2 text-sm text-gray-700 leading-snug whitespace-pre-line">
                                                {label}
                                                {coming_soon && (
                                                    <span
                                                        style={{ padding: '3px 8px', marginTop: 4 }}
                                                        className="w-fit flex flex-center bg-yellow-50 text-2xs text-yellow-400 italic rounded-full">
                                                        {t('coming soon')}
                                                    </span>
                                                )}
                                            </td>
                                            {visibleRoles.map((role) => {
                                                const isSystem = !!role.is_system
                                                const isOwnerOnly = !!owner_only
                                                const value = isSystem
                                                    ? resolveSystemPerm(role.id, key, isOwnerOnly)
                                                    : isOwnerOnly
                                                        ? false  // custom role không bao giờ có owner_only
                                                        : !!(role.permissions?.[key])

                                                return (
                                                    <td key={role.id} className="px-3 py-2 text-center">
                                                        <CheckboxCell
                                                            value={value}
                                                            disabled={isSystem || !!coming_soon}
                                                            coming_soon={coming_soon}
                                                            onChange={(checked) =>
                                                                handleToggle(role.id, key, checked)
                                                            }
                                                        />
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    )
}

export default nest(
    withProps({lazy: true})(DelayRender),
    OrganizationPermissionMatrix
)